(() => {
  'use strict';

  if (window.CamelusSupplementProductPage) return;
  window.CamelusSupplementProductPage = true;

  const PANEL_SELECTOR = '[data-camelus-supplement-panel]';
  const ADD_BUTTON_SELECTOR = '[data-camelus-native-atc]';

  function parseProductJSON(scope) {
    const holder =
      scope.closest('[data-product-form-wrapper]') ||
      scope.closest('[data-section-id]') ||
      document;

    const script =
      holder.querySelector('script[data-product-json]') ||
      document.querySelector('script[data-product-json]');

    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn(
        'Camelus modern product page: product JSON could not be parsed.',
        error
      );
      return null;
    }
  }

  function formatMoney(cents) {
    const value = Number(cents || 0);

    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      const format =
        (window.theme &&
          (window.theme.moneyFormat || window.theme.money_format)) ||
        'R{{amount}}';

      try {
        return window.Shopify.formatMoney(value, format);
      } catch (error) {
        // Use the stable local formatter below.
      }
    }

    return `R${(value / 100).toLocaleString('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  function formatButtonMoney(cents) {
    const value = Math.max(0, Number(cents || 0));
    const fixed = (value / 100).toFixed(2);
    const [whole, decimal] = fixed.split('.');
    const spacedWhole = whole.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ' '
    );

    return `R ${spacedWhole}.${decimal}`;
  }

  function optionIndexFor(wrapper) {
    const position = Number(wrapper.dataset.optionPosition || 0);
    return Math.max(0, position - 1);
  }

  function matchingVariantPrice(product, optionIndex, value) {
    if (!product || !Array.isArray(product.variants)) return 0;

    const matches = product.variants.filter((variant) => {
      const optionValue = Array.isArray(variant.options)
        ? variant.options[optionIndex]
        : variant[`option${optionIndex + 1}`];

      return String(optionValue) === String(value);
    });

    const available = matches.filter(
      (variant) => variant.available !== false
    );
    const pool = available.length ? available : matches;
    const prices = pool
      .map((variant) => Number(variant.price || 0))
      .filter((price) => price > 0);

    return prices.length ? Math.min(...prices) : 0;
  }

  /*
   * Pipeline's original variant controls combine label clicks with hover
   * preview behaviour. On touch devices the first tap can be consumed as a
   * hover/preview interaction. Select the radio directly on a clean touch or
   * pen tap, then suppress the synthetic follow-up label click.
   */
  function bindInstantVariantTap(input, label) {
    if (
      !input ||
      !label ||
      label.dataset.camelusInstantVariantTap === 'true'
    ) {
      return;
    }

    label.dataset.camelusInstantVariantTap = 'true';

    let pointerStart = null;
    let suppressClickUntil = 0;

    label.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse') return;

        pointerStart = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY
        };
      },
      { passive: true }
    );

    label.addEventListener(
      'pointerup',
      (event) => {
        if (
          event.pointerType === 'mouse' ||
          !pointerStart ||
          pointerStart.id !== event.pointerId
        ) {
          pointerStart = null;
          return;
        }

        const distance = Math.hypot(
          event.clientX - pointerStart.x,
          event.clientY - pointerStart.y
        );

        pointerStart = null;

        if (distance > 12 || input.disabled) return;

        event.preventDefault();
        event.stopPropagation();

        suppressClickUntil = Date.now() + 700;

        if (!input.checked) {
          input.click();
        }
      },
      { passive: false }
    );

    label.addEventListener(
      'pointercancel',
      () => {
        pointerStart = null;
      },
      { passive: true }
    );

    label.addEventListener(
      'click',
      (event) => {
        if (Date.now() >= suppressClickUntil) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  }

  function isOneLitreSize(value) {
    const normalised = String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9.]/g, '');

    return [
      '1l',
      '1litre',
      '1liter',
      '1000ml'
    ].includes(normalised);
  }

  function enhanceSizeOption(wrapper, product) {
    if (wrapper.dataset.camelusEnhancedSize === 'true') return;

    wrapper.dataset.camelusEnhancedSize = 'true';
    wrapper.classList.add('camelus-option--size');

    const optionIndex = optionIndexFor(wrapper);

    wrapper.querySelectorAll('.radio__button').forEach((button) => {
      const input = button.querySelector('input[type="radio"]');
      const label = button.querySelector('label');

      if (!input || !label) return;

      bindInstantVariantTap(input, label);

      const firstVisibleSpan = Array.from(label.children).find(
        (child) => !child.classList.contains('visually-hidden')
      );

      if (firstVisibleSpan) {
        firstVisibleSpan.classList.add('camelus-size-name');
      } else {
        const name = document.createElement('span');
        name.className = 'camelus-size-name';
        name.textContent = input.value;
        label.prepend(name);
      }

      if (
        isOneLitreSize(input.value) &&
        !label.querySelector('.camelus-most-popular-pill')
      ) {
        const badge = document.createElement('span');
        badge.className = 'camelus-most-popular-pill';
        badge.textContent = 'POPULAR';
        badge.setAttribute('aria-hidden', 'true');
        label.append(badge);
      }

      if (!label.querySelector('.camelus-size-price')) {
        const price = matchingVariantPrice(
          product,
          optionIndex,
          input.value
        );

        if (price > 0) {
          const priceElement = document.createElement('span');
          priceElement.className = 'camelus-size-price';
          priceElement.textContent = formatMoney(price);
          label.append(priceElement);
        }
      }
    });
  }

  function syncFlavourDot(label, dot) {
    const computed = window.getComputedStyle(label);
    const backgroundImage =
      label.style.backgroundImage || computed.backgroundImage;
    const swatch =
      label.style.getPropertyValue('--swatch') ||
      computed.getPropertyValue('--swatch');

    if (backgroundImage && backgroundImage !== 'none') {
      dot.style.backgroundImage = backgroundImage;
    }

    if (swatch) {
      dot.style.setProperty('--swatch', swatch.trim());
      dot.style.backgroundColor = swatch.trim();
    }
  }

  function enhanceFlavourOption(wrapper) {
    if (wrapper.dataset.camelusEnhancedFlavour === 'true') return;

    wrapper.dataset.camelusEnhancedFlavour = 'true';
    wrapper.classList.add('camelus-option--flavour');

    wrapper
      .querySelectorAll(
        'radio-swatch.swatch__button, .swatch__button'
      )
      .forEach((swatch) => {
        const input = swatch.querySelector('input[type="radio"]');
        const label = swatch.querySelector('label');

        if (!input || !label) return;

        bindInstantVariantTap(input, label);

        if (!label.querySelector('.camelus-flavour-dot')) {
          const dot = document.createElement('span');
          dot.className = 'camelus-flavour-dot';
          dot.setAttribute('aria-hidden', 'true');

          const name = document.createElement('span');
          name.className = 'camelus-flavour-name';
          name.textContent = input.value;

          label.append(dot, name);
          syncFlavourDot(label, dot);

          const observer = new MutationObserver(() => {
            syncFlavourDot(label, dot);
          });

          observer.observe(label, {
            attributes: true,
            attributeFilter: ['style', 'data-swatch']
          });

          window.setTimeout(() => syncFlavourDot(label, dot), 120);
          window.setTimeout(() => syncFlavourDot(label, dot), 600);
        }
      });

    const radioInputs = wrapper.querySelector(
      '.radio__inputs, .radio__inputs--swatch'
    );
    const guideButton = wrapper.querySelector('#openFlavourGuide');
    const guideRow = guideButton && guideButton.parentElement;

    if (radioInputs && guideRow && guideRow !== radioInputs) {
      guideRow.classList.add('camelus-flavour-guide-row');

      if (guideRow.previousElementSibling !== radioInputs) {
        radioInputs.insertAdjacentElement('afterend', guideRow);
      }
    }
  }

  function enhanceVariants(panel) {
    const holder =
      panel.closest('[data-product-form-wrapper]') ||
      panel.closest('[data-section-id]') ||
      document;

    const variantsBlock = holder.querySelector(
      '.product__block__variants'
    );

    if (!variantsBlock) return;

    variantsBlock.classList.add('camelus-variants-ready');
    const product = parseProductJSON(panel);

    variantsBlock
      .querySelectorAll(
        '[data-swapper-wrapper][data-select-label]'
      )
      .forEach((wrapper) => {
        const optionName = String(
          wrapper.dataset.selectLabel || ''
        ).trim();

        if (/size|volume|capacity/i.test(optionName)) {
          enhanceSizeOption(wrapper, product);
        }

        if (/flavou?r|taste/i.test(optionName)) {
          enhanceFlavourOption(wrapper);
        }
      });
  }

  /*
   * When the modern panel is enabled on an existing template, neutralise the
   * template's older standalone quantity input so only one quantity value is
   * submitted. This avoids rebuilding or duplicating the product template.
   */
  function disableLegacyQuantity(panel) {
    if (panel.dataset.camelusLegacyQuantityHandled === 'true') return;

    const modernInput = panel.querySelector(
      '.camelus-quantity__input[name="quantity"]'
    );
    const form = modernInput && modernInput.form;

    if (!modernInput || !form) return;

    panel.dataset.camelusLegacyQuantityHandled = 'true';

    document.querySelectorAll('input[name="quantity"]').forEach((input) => {
      if (input === modernInput || input.form !== form) return;

      input.dataset.camelusOriginalQuantityName = 'quantity';
      input.removeAttribute('name');
      input.disabled = true;
      input.setAttribute('aria-hidden', 'true');

      const wrapper =
        input.closest('.product__block') ||
        input.closest('.product__block__quantity') ||
        input.closest('[data-quantity-selector]') ||
        input.parentElement;

      if (wrapper && !wrapper.contains(panel)) {
        wrapper.classList.add('camelus-legacy-quantity-hidden');
        wrapper.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function selectedVariantForForm(product, form) {
    if (!product || !Array.isArray(product.variants) || !form) {
      return null;
    }

    const variantInput = form.querySelector('input[name="id"]');
    const variantId = variantInput && String(variantInput.value || '');

    if (variantId) {
      const byId = product.variants.find(
        (variant) => String(variant.id) === variantId
      );

      if (byId) return byId;
    }

    const selectedOptions = Array.from(
      form.querySelectorAll(
        '[data-swapper-wrapper][data-option-position]'
      )
    )
      .sort(
        (a, b) =>
          Number(a.dataset.optionPosition || 0) -
          Number(b.dataset.optionPosition || 0)
      )
      .map((wrapper) => {
        const checked = wrapper.querySelector(
          'input[type="radio"]:checked'
        );

        return checked ? String(checked.value) : '';
      });

    if (!selectedOptions.length) return null;

    return (
      product.variants.find((variant) => {
        const options = Array.isArray(variant.options)
          ? variant.options
          : [
              variant.option1,
              variant.option2,
              variant.option3
            ];

        return selectedOptions.every(
          (value, index) =>
            !value || String(options[index] || '') === value
        );
      }) || null
    );
  }

  function initialiseQuantityOfferButtons(panel) {
    if (panel.dataset.camelusQuantityOffersReady === 'true') return;

    const quantityInput = panel.querySelector(
      '.camelus-quantity__input[name="quantity"]'
    );
    const offerButtons = panel.querySelectorAll(
      '[data-camelus-quantity-offer]'
    );

    if (!quantityInput || !offerButtons.length) return;

    panel.dataset.camelusQuantityOffersReady = 'true';

    const syncOfferButtons = () => {
      const currentQuantity = Math.max(
        1,
        Math.floor(Number(quantityInput.value || 1))
      );

      offerButtons.forEach((button) => {
        const offerQuantity = Number(
          button.dataset.camelusQuantityOffer || 0
        );
        const selected = currentQuantity === offerQuantity;

        button.classList.toggle('is-selected', selected);
        button.setAttribute(
          'aria-pressed',
          selected ? 'true' : 'false'
        );
      });
    };

    const scheduleSync = () => {
      syncOfferButtons();
      window.requestAnimationFrame(syncOfferButtons);
      window.setTimeout(syncOfferButtons, 60);
      window.setTimeout(syncOfferButtons, 180);
    };

    offerButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const quantity = Math.max(
          1,
          Math.floor(
            Number(button.dataset.camelusQuantityOffer || 1)
          )
        );

        quantityInput.value = String(quantity);
        quantityInput.dispatchEvent(
          new Event('input', { bubbles: true })
        );
        quantityInput.dispatchEvent(
          new Event('change', { bubbles: true })
        );

        scheduleSync();
      });
    });

    quantityInput.addEventListener('input', syncOfferButtons);
    quantityInput.addEventListener('change', syncOfferButtons);

    panel
      .querySelectorAll(
        '[data-decrease-quantity], [data-increase-quantity]'
      )
      .forEach((control) => {
        control.addEventListener('click', scheduleSync);
      });

    syncOfferButtons();
  }

  /*
   * The price shown inside Add to Cart is the selected variant price
   * multiplied by the current quantity.
   */
  function initialiseButtonTotal(panel) {
    if (panel.dataset.camelusButtonTotalReady === 'true') return;

    const button = panel.querySelector(ADD_BUTTON_SELECTOR);
    const priceElement =
      button && button.querySelector('[data-button-price]');
    const quantityInput = panel.querySelector(
      '.camelus-quantity__input[name="quantity"]'
    );
    const form = button && button.closest('form');
    const product = parseProductJSON(panel);

    if (!button || !priceElement || !quantityInput || !form || !product) {
      return;
    }

    panel.dataset.camelusButtonTotalReady = 'true';

    const updateTotal = () => {
      const variant = selectedVariantForForm(product, form);
      const fallbackUnitPrice = Number(button.dataset.unitPrice || 0);
      const unitPrice = Number(
        variant && variant.price ? variant.price : fallbackUnitPrice
      );
      const quantity = Math.max(
        1,
        Math.floor(Number(quantityInput.value || 1))
      );

      if (!unitPrice) return;

      button.dataset.unitPrice = String(unitPrice);
      priceElement.textContent = formatButtonMoney(
        unitPrice * quantity
      );
    };

    const scheduleUpdate = () => {
      updateTotal();
      window.requestAnimationFrame(updateTotal);
      window.setTimeout(updateTotal, 40);
      window.setTimeout(updateTotal, 140);
    };

    quantityInput.addEventListener('input', updateTotal);
    quantityInput.addEventListener('change', updateTotal);

    panel
      .querySelectorAll(
        '[data-decrease-quantity], [data-increase-quantity]'
      )
      .forEach((control) => {
        control.addEventListener('click', scheduleUpdate);
      });

    form.addEventListener('change', scheduleUpdate);

    [
      'theme:variant:change',
      'theme:variant:changed',
      'variant:change',
      'product:variant-change'
    ].forEach((eventName) => {
      document.addEventListener(eventName, scheduleUpdate);
    });

    const variantInput = form.querySelector('input[name="id"]');

    if (variantInput) {
      const observer = new MutationObserver(scheduleUpdate);
      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    }

    updateTotal();
  }

  /*
   * Visual spinner support only.
   * This never prevents submit, never fetches cart/add.js and never redirects.
   * Pipeline still performs the real native AJAX product submission.
   */
  function initialiseNativeAddButton(panel) {
    if (panel.dataset.camelusNativeAtcReady === 'true') return;

    const button = panel.querySelector(ADD_BUTTON_SELECTOR);
    const form = button && button.closest('form');

    if (!button || !form) return;

    panel.dataset.camelusNativeAtcReady = 'true';

    let fallbackTimer = null;
    let successTimer = null;

    const clearTimers = () => {
      window.clearTimeout(fallbackTimer);
      window.clearTimeout(successTimer);
      fallbackTimer = null;
      successTimer = null;
    };

    const resetButton = () => {
      clearTimers();
      button.classList.remove(
        'camelus-is-loading',
        'camelus-is-success'
      );
      button.removeAttribute('aria-busy');
    };

    const showLoading = () => {
      if (button.disabled) return;

      clearTimers();
      button.classList.remove('camelus-is-success');
      button.classList.add('camelus-is-loading');
      button.setAttribute('aria-busy', 'true');

      fallbackTimer = window.setTimeout(resetButton, 12000);
    };

    const showSuccess = () => {
      if (!button.classList.contains('camelus-is-loading')) return;

      clearTimers();
      button.classList.remove('camelus-is-loading');
      button.classList.add('camelus-is-success');
      button.removeAttribute('aria-busy');

      successTimer = window.setTimeout(() => {
        button.classList.remove('camelus-is-success');
      }, 850);
    };

    button.addEventListener('click', () => {
      window.requestAnimationFrame(showLoading);
    });

    form.addEventListener('submit', showLoading, { capture: true });
    document.addEventListener('theme:cart:change', showSuccess);

    const errorDisplay = form.querySelector('[data-error-display]');

    if (errorDisplay) {
      const errorObserver = new MutationObserver(() => {
        if (errorDisplay.textContent.trim()) {
          resetButton();
        }
      });

      errorObserver.observe(errorDisplay, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  /*
   * Native Pipeline popdown bridge.
   * It waits for theme:cart:change before opening the existing cart drawer.
   */
  function installCartDrawerBridge() {
    if (window.__camelusCartDrawerBridgeInstalled) return;
    window.__camelusCartDrawerBridgeInstalled = true;

    document.addEventListener('theme:cart:popdown', () => {
      window.__cartDrawerShouldInstantUpdate = true;

      if (window.__camelusOpenDrawerAfterCartUpdate) {
        document.removeEventListener(
          'theme:cart:change',
          window.__camelusOpenDrawerAfterCartUpdate
        );
      }

      const openDrawerAfterCartUpdate = () => {
        const drawer = document.querySelector(
          '[data-drawer="drawer-cart"]'
        );

        if (drawer) {
          drawer.dispatchEvent(
            new CustomEvent('theme:drawer:open', {
              bubbles: false
            })
          );
        }

        document.removeEventListener(
          'theme:cart:change',
          openDrawerAfterCartUpdate
        );

        if (
          window.__camelusOpenDrawerAfterCartUpdate ===
          openDrawerAfterCartUpdate
        ) {
          window.__camelusOpenDrawerAfterCartUpdate = null;
        }
      };

      window.__camelusOpenDrawerAfterCartUpdate =
        openDrawerAfterCartUpdate;

      document.addEventListener(
        'theme:cart:change',
        openDrawerAfterCartUpdate
      );
    });
  }

  function initialise(root = document) {
    root.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
      if (panel.dataset.camelusInitialised !== 'true') {
        panel.dataset.camelusInitialised = 'true';
        enhanceVariants(panel);
      }

      disableLegacyQuantity(panel);
      initialiseQuantityOfferButtons(panel);
      initialiseButtonTotal(panel);
      initialiseNativeAddButton(panel);
    });
  }

  installCartDrawerBridge();

  document.addEventListener('DOMContentLoaded', () => {
    initialise(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialise(event.target);
  });

  if (document.readyState !== 'loading') {
    initialise(document);
  }
})();
